import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTheme, Headline, Divider, TouchableRipple, ActivityIndicator, Portal, Modal } from "react-native-paper";
import { Spacer } from "../../components/spacer/spacer";
import { View, StyleSheet, Image, Linking } from "react-native";
import { StackActions, useFocusEffect, useNavigation } from "@react-navigation/native";
import { signOutUser } from "../../redux/actions/actionCreators";
import { Text, Screen, Button, InfoBox } from "../../components";
import { Stack } from "../../components/layout/stack";
import { ThemeType } from "../../theme/theme";
import { Inline } from "../../components/layout/inline";
import { DOMUSO_ZEN_DESK_LINK, DOMUSO_LEGAL_LINK } from "../../utils";
import { useAuthData } from "../../hooks/useAuthData";
import { RootState } from "../../redux/reducers";
import { addReservations } from "../../redux/actions/actionCreators";
import { DomusoApiService } from "../../api/DomusoApiService";
import { InfoBoxIconType, InfoBoxType, ButtonVariant } from "../../enums";
import { translate } from "../../i18n";
import { fontSize } from "../../theme/fontSize";

export const MyUnits = ({ route }: any) => {
  const { userInfo } = useAuthData(logger);
  const { family_name, given_name } = userInfo || {};
  const navigation = useNavigation();
  const [isLoading, setLoading] = useState(false);
  const [isremoveLoading, setremoveLoading] = useState(false);
  const [isModalVisible, setModalVisible] = useState(false);
  const [resIdToRemove, setResIdToRemove] = useState<any>(null);
  const reservations = useSelector((state: RootState) => state.reservation.reservations);
  const { params } = route;
  const dispatch = useDispatch();
  const logger = useSelector((state: RootState) => state.logger);
  const cognitoUser = useSelector((state: RootState) => state.auth.cognitoUser);
  const theme = useTheme() as ThemeType;
  const styles = getStyles(theme);
  const domusoApiService = new DomusoApiService(cognitoUser, dispatch);
  const [hasBlacklistedReservation, setHasBlacklistedReservation] = useState(false);
  const [allowUnitRemoval, setAllowUnitRemoval] = useState(true);

  const layoutEffect = useCallback(() => {
    setLoading(true);
    domusoApiService
      .getReservations()
      .then(async (resp) => {
        if (!resp || !resp.length) return;
        resp = resp.filter(({ status }: any) => status !== "CANCELLED_WITHOUT_FEE");
        const isBlacklisted = resp.some(({ is_blacklisted }: any) => is_blacklisted);
        setHasBlacklistedReservation(isBlacklisted);
        const shouldAllowUnitRemoval = resp.length > 1 && !isBlacklisted;
        setAllowUnitRemoval(shouldAllowUnitRemoval);
        dispatch(addReservations(resp));
        // reset home stack
        navigation.dispatch(
          StackActions.replace("Home")
        );
        setLoading(false);
      })
      .catch((error) => {
        logger.error(error);
        setLoading(false);
      });
  }, []);
  useFocusEffect(layoutEffect);

  const onRemoveUnitHandler = (id: string) => {
    setModalVisible(true);
    setResIdToRemove(id);
  }

  const onRemoveConfirmation = async () => {
    setremoveLoading(true);
    try {
      logger.info(`Hiding reservation with id ${resIdToRemove}`);
      await domusoApiService.hideReservation(resIdToRemove);
      const newReservations = reservations.filter((res: any) => res.id !== resIdToRemove);
      dispatch(addReservations(newReservations));
    }
    catch (error) {
      logger.error(error);
    }
    setremoveLoading(false);
    setModalVisible(false);
  }

  const renderContent = () => {
    if (isLoading) {
      return <ActivityIndicator />;
    } else {
      return (
        <>
          <RemoveReservationModal
            styles={styles}
            onConfirmation={onRemoveConfirmation}
            isVisible={isModalVisible}
            setVisible={setModalVisible}
            isLoading={isremoveLoading}
          />
          <Stack space="small">
            {reservations.map((reservation) => (
              <>
                <RemoveReservationCard
                  styles={styles}
                  reservation={reservation}
                  allowUnitRemoval={allowUnitRemoval}
                  onRemove={onRemoveUnitHandler}
                />
                <Divider />
              </>
            ))}
          </Stack>
        </>
      );
    }
  };

  return (
    <Screen
      style={styles.container}
      unsafe={true}
      preset="scroll"
      statusBar="dark-content"
      backgroundColor={theme.colors.background}>
      <View>
        {renderContent()}
      </View>
    </Screen>
  );
};

const getStyles = (theme: ThemeType) => {
  const { colors, spacing, fontSize } = theme;
  return StyleSheet.create({
    container: {
      display: "flex",
      justifyContent: "space-between",
      backgroundColor: colors.background,
      marginTop: spacing.medium,
      marginBottom: spacing.large,
    },
    modalContainer: {
      display: "flex",
      marginHorizontal: spacing.medium,
      backgroundColor: colors.background,
      borderRadius: 10,
      flexDirection: "column",
      paddingVertical: spacing.small,
      paddingHorizontal: spacing.large,
      paddingTop: spacing.large,
    },
    text: {
      fontSize: theme.fontSize.large,
      color: colors.text,
    },
    removeReservationCard: {
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      paddingBottom: spacing.small,
    },
    removeButton: {
      width: 110,
    },
    modalButtonContainer: {
      display: "flex",
      flexDirection: "row",
      justifyContent: "flex-end",
      marginTop: spacing.medium,
    },
    modalButton: {
      width: "46%",
      marginLeft: spacing.smaller,
    },
  });
};

interface IRemoveReservationCardParams {
  reservation: any,
  styles: any,
  onRemove: Function,
  allowUnitRemoval: boolean,
}

const RemoveReservationCard = ({ reservation, styles, onRemove, allowUnitRemoval }: IRemoveReservationCardParams) => {
  const unitName = reservation.property_unit ? `Unit #${reservation.property_unit}` : 'Unit Unassigned';
  return (
    <View style={styles.removeReservationCard}>
      <View>
        <Text style={styles.text}>{unitName}</Text>
        <Text style={[styles.text, { fontSize: fontSize.medium }]}>{reservation.property.name}</Text>
      </View>
      {allowUnitRemoval && (
        <Button
          style={styles.removeButton}
          variant={ButtonVariant.LIGHT}
          mode="contained"
          onPress={() => onRemove(reservation.id)}
          tx="componentStatic.remove"
        />
      )}
    </View>
  );
}

interface IRemoveReservationModalParams {
  styles: any,
  isVisible: boolean,
  setVisible: Function,
  onConfirmation: Function,
  isLoading: boolean,
}

const RemoveReservationModal = ({
  styles,
  onConfirmation,
  isVisible,
  setVisible,
  isLoading
}: IRemoveReservationModalParams) => (
    <Portal>
      <Modal
        visible={isVisible}
        onDismiss={() => setVisible(false)}
        contentContainerStyle={styles.modalContainer}>
        <Stack space="medium">
          <Text tx="moreStack.removeAUnit" style={[styles.text, { fontWeight: "bold" }]} />
          <Text tx="moreStack.removeAUnitMessage" style={styles.text} />
          <View style={styles.modalButtonContainer}>
            <Button
              variant={ButtonVariant.LIGHT}
              mode="contained"
              disabled={isLoading}
              onPress={() => setVisible(false)}
              tx="componentStatic.cancel"
              style={styles.modalButton}
            />
            <Button
              mode="contained"
              loading={isLoading}
              onPress={() => onConfirmation()}
              tx="componentStatic.confirm"
              style={styles.modalButton}
            />
          </View>
        </Stack>
      </Modal>
    </Portal>
  )